import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Box,
  Paper,
  Typography,
  Grid2,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Income = ({ rentHistories }) => {
  const today = dayjs();
  const [startDate, setStartDate] = useState(today.subtract(5, 'month').startOf('month'));
  const [endDate, setEndDate] = useState(today.endOf('month'));
  const [incomeData, setIncomeData] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(today.subtract(5, 'month').startOf('month'));
  const [selectedEndDate, setSelectedEndDate] = useState(today.endOf('month'));

  const calculateMonthlyIncome = (rentHistory) => {
    const start = dayjs(rentHistory.rent_start);
    const end = dayjs(rentHistory.rent_end);
    const monthlyIncome = {};

    // Get all months between start and end dates
    let currentMonth = start.startOf('month');
    const lastMonth = end.endOf('month');

    while (currentMonth.isBefore(lastMonth) || currentMonth.isSame(lastMonth, 'month')) {
      const monthKey = currentMonth.format('MMM YYYY');
      const daysInMonth = currentMonth.daysInMonth();
      const monthlyRent = parseFloat(rentHistory.rent_amount);

      // Calculate income for this month
      let monthIncome;
      if (currentMonth.isSame(start, 'month')) {
        // First month - calculate partial month
        const daysCovered = daysInMonth - start.date() + 1;
        monthIncome = (monthlyRent / daysInMonth) * daysCovered;
      } else if (currentMonth.isSame(end, 'month')) {
        // Last month - calculate partial month
        const daysCovered = end.date();
        monthIncome = (monthlyRent / daysInMonth) * daysCovered;
      } else {
        // Full month - use full rent amount
        monthIncome = monthlyRent;
      }

      monthlyIncome[monthKey] = {
        month: currentMonth.format('MMM'),
        year: currentMonth.format('YYYY'),
        amount: Math.round(monthIncome * 100) / 100 // Round to 2 decimal places
      };

      currentMonth = currentMonth.add(1, 'month');
    }

    return monthlyIncome;
  };

  const handleApply = () => {
    // Ensure we're using the start and end of the selected months
    const newStartDate = selectedStartDate.startOf('month');
    const newEndDate = selectedEndDate.endOf('month');
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  useEffect(() => {
    if (!rentHistories || rentHistories.length === 0) {
      console.log('No rent histories available');
      setIncomeData([]);
      return;
    }

    console.log('Processing rent histories:', rentHistories);
    console.log('Date range:', {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD')
    });

    // Calculate income for each rent history
    const allMonthlyIncome = {};
    rentHistories.forEach(history => {
      console.log('Processing history:', history);
      const monthlyIncome = calculateMonthlyIncome(history);
      console.log('Calculated monthly income:', monthlyIncome);
      
      Object.entries(monthlyIncome).forEach(([key, value]) => {
        if (!allMonthlyIncome[key]) {
          allMonthlyIncome[key] = value;
        } else {
          // If there's overlapping rent history, add the amounts
          allMonthlyIncome[key].amount += value.amount;
        }
      });
    });

    // Convert to array and sort by date
    const sortedIncome = Object.entries(allMonthlyIncome)
      .map(([key, value]) => ({
        month: value.month,
        year: value.year,
        amount: value.amount
      }))
      .sort((a, b) => {
        const dateA = dayjs(`${a.year}-${a.month}`);
        const dateB = dayjs(`${b.year}-${b.month}`);
        return dateA.diff(dateB);
      });

    console.log('Sorted income data:', sortedIncome);

    // Filter by selected date range
    const filteredIncome = sortedIncome.filter(item => {
      // Create a date string in YYYY-MM format for proper parsing
      const itemDateStr = `${item.year}-${item.month}`;
      const itemDate = dayjs(itemDateStr, 'YYYY-MMM');
      
      // Ensure we're comparing dates at the start of the month
      const start = startDate.startOf('month');
      const end = endDate.endOf('month');
      
      const isInRange = itemDate.isAfter(start.subtract(1, 'day')) && 
                       itemDate.isBefore(end.add(1, 'day'));
      
      console.log('Checking date:', {
        itemDate: itemDate.format('YYYY-MM-DD'),
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
        isInRange
      });
      
      return isInRange;
    });

    console.log('Final filtered income data:', filteredIncome);
    setIncomeData(filteredIncome);
  }, [rentHistories, startDate, endDate]);

  const chartData = {
    labels: incomeData.map(item => `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Monthly Income',
        data: incomeData.map(item => item.amount),
        fill: false,
        borderColor: '#2196f3',
        backgroundColor: '#2196f3',
        tension: 0.4,
        borderWidth: 4,
        pointRadius: 6,
        pointBackgroundColor: '#2196f3',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: '#2196f3',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  };

  // Get currency from the first rent history
  const currency = rentHistories && rentHistories.length > 0 ? rentHistories[0].rent_currency : 'USD';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        }
      },
      title: {
        display: true,
        text: 'Monthly Income Overview',
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#333'
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `Amount: ${currency} ${context.raw.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: `Amount (${currency})`,
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        },
        ticks: {
          callback: function(value) {
            return `${currency} ${value.toFixed(2)}`;
          },
          color: '#333'
        }
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: 'Month',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        },
        ticks: {
          color: '#333'
        }
      }
    },
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Income
      </Typography>
      <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          <Grid2 xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Month"
                value={selectedStartDate}
                onChange={(newValue) => setSelectedStartDate(newValue)}
                views={['month', 'year']}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Month"
                value={selectedEndDate}
                onChange={(newValue) => setSelectedEndDate(newValue)}
                views={['month', 'year']}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="contained" 
              onClick={handleApply}
              fullWidth
              sx={{ height: '56px' }}
            >
              Apply
            </Button>
          </Grid2>
        </Grid2>

        <Box sx={{ mb: 4, height: '400px', position: 'relative', backgroundColor: '#fff' }}>
          <Line data={chartData} options={chartOptions} />
        </Box>
      </Paper>
    </>
  );
};

export default Income; 